import type { Control, FieldErrors, Path, UseFormGetValues, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { Controller, useFieldArray, useWatch } from "react-hook-form";
import type { CreateProductInput, CustomFeatureItem, UpdateProductInput } from "@/types/activation";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

// 扩展CustomFeatureItem类型以包含id属性（用于React key）
export interface FormCustomFeatureItem extends CustomFeatureItem {
  id?: string; // Made id optional to align with Zod schema for resolver
}

// 定义表单中customFeatures的类型（覆盖原始类型）
interface FormValuesWithCustomFeatures extends Omit<CreateProductInput | UpdateProductInput, 'customFeatures'> {
  customFeatures?: FormCustomFeatureItem[]; // Made optional to align with Zod schema defaults
}

interface ProductFeatureInputProps {
  control: Control<FormValuesWithCustomFeatures>;
  register: UseFormRegister<FormValuesWithCustomFeatures>;
  errors: FieldErrors<FormValuesWithCustomFeatures>;
  setValue: UseFormSetValue<FormValuesWithCustomFeatures>;
  getValues: UseFormGetValues<FormValuesWithCustomFeatures>;
}

interface FeatureItemRowProps {
  index: number;
  control: Control<FormValuesWithCustomFeatures>;
  register: UseFormRegister<FormValuesWithCustomFeatures>;
  errors: FieldErrors<FormValuesWithCustomFeatures>;
  setValue: UseFormSetValue<FormValuesWithCustomFeatures>;
  remove: (index: number) => void;
  item: FormCustomFeatureItem; // 使用FormCustomFeatureItem类型
}

function FeatureItemRow({ index, control, register, errors, setValue, remove, item }: FeatureItemRowProps) {
  const { t } = useTranslation();
  const featureValueType = useWatch({
    control,
    name: `customFeatures.${index}.valueType` as const,
    defaultValue: item.valueType // Provide a default value for useWatch
  });

  return (
    <div key={item.id} className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 items-start"> {/* Removed p-3 border rounded-md */}
      {/* Key Name Input */}
      <div className="flex flex-col space-y-1.5">
        <Label htmlFor={`customFeatures.${index}.keyName`} className="text-xs">
          {t("admin.activation.products.featureKey")}
        </Label>
        <Input
          id={`customFeatures.${index}.keyName`}
          placeholder={t("admin.activation.products.featureKeyPlaceholder")}
          {...register(`customFeatures.${index}.keyName` as const)}
        />
        {errors.customFeatures?.[index]?.keyName && (
          <p className="text-sm text-destructive mt-1">{String(errors.customFeatures[index]?.keyName?.message)}</p>
        )}
      </div>

      {/* Value Input - Conditional based on type */}
      <div className="flex flex-col space-y-1.5">
        <Label htmlFor={`customFeatures.${index}.value`} className="text-xs">
          {t("admin.activation.products.featureValue")}
        </Label>
        <Controller
          name={`customFeatures.${index}.value` as const}
          control={control}
          render={({ field }) => {
            if (featureValueType === "boolean") {
              return (
                <div className="flex items-center h-10">
                  <Switch
                    checked={!!field.value}
                    onCheckedChange={(checked) => {
                      // 确保布尔值正确设置，而不是作为字符串
                      field.onChange(checked === true);
                      console.log("布尔值开关切换:", checked);
                    }}
                    aria-label={t("admin.activation.products.featureValue")}
                  />
                </div>
              );
            }

            // 类型安全处理输入值
            let inputValue: string | number = '';

            if (field.value !== undefined) {
              if (featureValueType === "number") {
                inputValue = typeof field.value === 'number' ? field.value :
                             Number.parseFloat(String(field.value)) || 0;
              } else {
                inputValue = String(field.value);
              }
            }

            return (
              <Input
                type={featureValueType === "number" ? "number" : "text"}
                placeholder={t("admin.activation.products.featureValuePlaceholder")}
                {...field}
                onChange={(e) => {
                  if (featureValueType === "number") {
                    const numValue = Number.parseFloat(e.target.value) || 0;
                    field.onChange(numValue);
                    console.log("数字输入改变:", numValue);
                  } else {
                    field.onChange(e.target.value);
                    console.log("文本输入改变:", e.target.value);
                  }
                }}
                value={inputValue}
              />
            );
          }}
        />
        {errors.customFeatures?.[index]?.value && (
          <p className="text-sm text-destructive mt-1">{String(errors.customFeatures[index]?.value?.message)}</p>
        )}
      </div>

      {/* Type Select */}
      <div className="flex flex-col space-y-1.5">
        <Label htmlFor={`customFeatures.${index}.valueType`} className="text-xs">
          {t("admin.activation.products.featureType")}
        </Label>
        <Controller
          name={`customFeatures.${index}.valueType` as const}
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onValueChange={(newType: "text" | "number" | "boolean") => {
                field.onChange(newType);
                let defaultValue: string | number | boolean = "";
                if (newType === "number") defaultValue = 0;
                if (newType === "boolean") defaultValue = false;
                setValue(`customFeatures.${index}.value` as Path<FormValuesWithCustomFeatures>, defaultValue);
              }}
            >
              <SelectTrigger id={`customFeatures.${index}.valueType`} className="w-[110px]">
                <SelectValue placeholder={t("admin.activation.products.selectType")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">{t("admin.activation.featureTypes.text")}</SelectItem>
                <SelectItem value="number">{t("admin.activation.featureTypes.number")}</SelectItem>
                <SelectItem value="boolean">{t("admin.activation.featureTypes.boolean")}</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* Remove Button */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => remove(index)}
        className="self-center" // Simplified alignment for the remove button
        aria-label={t("admin.activation.removeFeatureAriaLabel")}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function ProductFeatureInput({ control, register, errors, setValue, getValues }: ProductFeatureInputProps) {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "customFeatures",
  });

  const addNewFeature = () => {
    // 创建新的特性项，确保添加所有必要的字段
    const newFeature: FormCustomFeatureItem = {
      id: `feature-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      keyName: "",
      value: "",
      valueType: "text"
    };
    append(newFeature);
  };

  return (
    <div className="space-y-4">
      <Label>{t("admin.activation.products.features")}</Label>
      <div className="space-y-3">
        {fields.map((field, index) => (
          <FeatureItemRow
            key={field.id}
            item={field as unknown as FormCustomFeatureItem}
            index={index}
            control={control}
            register={register}
            errors={errors}
            setValue={setValue}
            remove={remove}
          />
        ))}
      </div>
      <Button type="button" variant="outline" onClick={addNewFeature} className="mt-3">
        {t("admin.activation.products.addCustomFeature")}
      </Button>
    </div>
  );
}